import { useEffect } from "react";

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  keywords?: string;
  structuredData?: object | object[];
}

function setMeta(name: string, content: string, attr: "name" | "property" = "name") {
  let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href: string) {
  let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setStructuredData(data: object | object[]) {
  // Remove any previously injected structured data scripts by this hook
  document.querySelectorAll('script[data-seo-structured]').forEach((el) => el.remove());

  const schemas = Array.isArray(data) ? data : [data];
  schemas.forEach((schema) => {
    const script = document.createElement("script");
    script.setAttribute("type", "application/ld+json");
    script.setAttribute("data-seo-structured", "true");
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
  });
}

export function useSEO({ title, description, canonical, keywords, structuredData }: SEOProps) {
  useEffect(() => {
    document.title = title;
    setMeta("description", description);
    if (keywords) setMeta("keywords", keywords);

    // Open Graph
    setMeta("og:title", title, "property");
    setMeta("og:description", description, "property");
    setMeta("og:type", "website", "property");

    // Twitter
    setMeta("twitter:title", title, "name");
    setMeta("twitter:description", description, "name");
    setMeta("twitter:card", "summary_large_image", "name");

    // Canonical
    const canonicalUrl = canonical ?? `https://www.sedistribution.com.my${window.location.pathname}`;
    setCanonical(canonicalUrl);

    // Last modified
    setMeta("last-modified", new Date().toISOString().split("T")[0]);

    // Structured data (JSON-LD)
    if (structuredData) {
      setStructuredData(structuredData);
    } else {
      // Clean up any leftover structured data from previous page
      document.querySelectorAll('script[data-seo-structured]').forEach((el) => el.remove());
    }
  }, [title, description, canonical, keywords, structuredData]);
}
